'use client';

import { CompanyPersonSelectFields } from '@/features/contacts/components';
import { MainButton, SecondaryButton, SectionTabs, SmartInput } from '@web/ui';

import styles from './QuickTaskInlineCreateForm.module.scss';

import type { CompanyOption } from '@/features/contacts/companies/components/CompanySelectField';
import type { PersonOption } from '@/features/contacts/people/components/PersonSelectField';

type ClientMode = 'client' | 'internal';

type InlineCreateErrors = Partial<
  Record<
    | 'description'
    | 'company_id'
    | 'person_id'
    | 'form'
    | 'title'
    | 'assignee_id'
    | 'service_type_id'
    | 'due_date'
    | 'level_of_effort',
    string
  >
>;

type QuickTaskInlineCreateFormProps = {
  clientMode: ClientMode;
  description: string;
  company: CompanyOption | null;
  person: PersonOption | null;
  isCreating: boolean;
  errors: InlineCreateErrors;
  onClientModeChange: (mode: ClientMode) => void;
  onDescriptionChange: (value: string) => void;
  onCompanyChange: (
    company: CompanyOption | null,
    nextPerson: PersonOption | null,
  ) => void;
  onPersonChange: (
    person: PersonOption | null,
    nextCompany: CompanyOption | null,
  ) => void;
  onCancel: () => void;
  onCreate: () => void;
};

const CONTEXT_SECTIONS = [
  { id: 'client', label: 'Client' },
  { id: 'internal', label: 'Internal' },
] as const;

export function QuickTaskInlineCreateForm({
  clientMode,
  description,
  company,
  person,
  isCreating,
  errors,
  onClientModeChange,
  onDescriptionChange,
  onCompanyChange,
  onPersonChange,
  onCancel,
  onCreate,
}: QuickTaskInlineCreateFormProps) {
  return (
    <div className={styles.quickTaskInlineCreateForm}>
      <div className={styles.quickTaskInlineCreateForm__contextBlock}>
        <p className={styles.quickTaskInlineCreateForm__sectionLabel}>
          Context
        </p>
        <SectionTabs
          sections={CONTEXT_SECTIONS.map((section) => ({
            id: section.id,
            label: section.label,
          }))}
          activeId={clientMode}
          onSectionChange={(nextValue) =>
            onClientModeChange(nextValue as ClientMode)
          }
          aria-label="Task context"
        />

        {clientMode === 'client' ? (
          <CompanyPersonSelectFields
            company={company}
            person={person}
            onCompanyChange={onCompanyChange}
            onPersonChange={onPersonChange}
            companyError={errors.company_id}
            personError={errors.person_id}
            companyLabel="Company"
            personLabel="Person"
            personPlaceholder="Optional person"
            layout="inline"
          />
        ) : null}
      </div>

      <SmartInput
        type="textarea"
        name="quick-task-inline-description"
        label="Description"
        value={description}
        error={errors.description}
        onChange={onDescriptionChange}
      />

      <div className={styles.quickTaskInlineCreateForm__actions}>
        <SecondaryButton
          ariaLabel="Cancel quick task creation"
          variant="medium"
          onClick={onCancel}
        >
          <span>Cancel</span>
        </SecondaryButton>
        <MainButton
          ariaLabel="Create quick task"
          onClick={onCreate}
          isLoading={isCreating}
        >
          Create Quick Task
        </MainButton>
      </div>

      <p className={styles.quickTaskInlineCreateForm__error}>
        {errors.form ?? ''}
      </p>
    </div>
  );
}

export type {
  ClientMode as QuickTaskInlineCreateClientMode,
  InlineCreateErrors,
};
